import { z } from "zod";

const capabilitySchema = z.enum([
  "text",
  "tools",
  "vision",
  "embeddings",
  "image_generation",
  "image_editing"
]);

export const llmModelSchema = z.object({
  public_name: z.string(),
  upstream_name: z.string(),
  capabilities: z.array(capabilitySchema).default([]),
  input_cost_per_million: z.number().nonnegative().default(0),
  output_cost_per_million: z.number().nonnegative().default(0)
});

export type LlmModel = z.infer<typeof llmModelSchema>;
export type McpTransport = "stdio" | "streamable_http" | "legacy_sse";
export type EndpointKind = "llm" | "mcp" | "a2a";
export type LlmProviderKind = "open_ai_compatible" | "anthropic" | "ollama" | "xai";
export type LlmConnectionPreset =
  | "openai_official"
  | "anthropic_official"
  | "xai_official"
  | "ollama_local"
  | "openai_compatible"
  | "custom";

export interface LlmConnection {
  kind: LlmProviderKind;
  baseUrl: string;
}

const OPENAI_URL = "https://api.openai.com";
const ANTHROPIC_URL = "https://api.anthropic.com";
const XAI_URL = "https://api.x.ai";
const OLLAMA_URL = "http://127.0.0.1:11434";

export function configurationRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? { ...value } : {};
}

export function llmModels(configuration: unknown): LlmModel[] {
  const parsed = z.array(llmModelSchema).safeParse(configurationRecord(configuration).models);
  return parsed.success ? parsed.data : [];
}

export function mcpTransport(configuration: unknown): McpTransport {
  const value = configurationRecord(configuration).transport;
  return value === "streamable_http" || value === "legacy_sse" ? value : "stdio";
}

export function detectLlmPreset(connection: LlmConnection): LlmConnectionPreset {
  const baseUrl = connection.baseUrl.replace(/\/$/, "");
  if (connection.kind === "open_ai_compatible" && baseUrl === OPENAI_URL) {
    return "openai_official";
  }
  if (connection.kind === "anthropic" && baseUrl === ANTHROPIC_URL) {
    return "anthropic_official";
  }
  if (connection.kind === "xai" && baseUrl === XAI_URL) {
    return "xai_official";
  }
  if (connection.kind === "ollama" && baseUrl === OLLAMA_URL) return "ollama_local";
  if (connection.kind === "open_ai_compatible") return "openai_compatible";
  return "custom";
}

export function llmConnection(configuration: unknown): LlmConnection {
  const record = configurationRecord(configuration);
  const kind =
    record.kind === "anthropic" || record.kind === "open_ai_compatible" || record.kind === "xai"
      ? record.kind
      : "ollama";
  return {
    kind,
    baseUrl: typeof record.base_url === "string" ? record.base_url : OLLAMA_URL
  };
}

export function applyLlmPreset(preset: LlmConnectionPreset, current: LlmConnection): LlmConnection {
  switch (preset) {
    case "openai_official":
      return { kind: "open_ai_compatible", baseUrl: OPENAI_URL };
    case "anthropic_official":
      return { kind: "anthropic", baseUrl: ANTHROPIC_URL };
    case "xai_official":
      return { kind: "xai", baseUrl: XAI_URL };
    case "ollama_local":
      return { kind: "ollama", baseUrl: OLLAMA_URL };
    case "openai_compatible":
      return {
        kind: "open_ai_compatible",
        baseUrl: detectLlmPreset(current) === "openai_compatible" ? current.baseUrl : ""
      };
    case "custom":
      return current;
  }
}

export function llmAdapterLabel(configuration: unknown) {
  switch (configurationRecord(configuration).kind) {
    case "open_ai_compatible":
      return "OpenAI compatible";
    case "anthropic":
      return "Anthropic";
    case "xai":
      return "xAI (Grok)";
    case "ollama":
      return "Ollama";
    default:
      return "LLM";
  }
}

export function parseAdvancedConfiguration(value: FormDataEntryValue | null) {
  const parsed = JSON.parse(typeof value === "string" ? value : "{}") as unknown;
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Advanced configuration must be a JSON object");
  }
  return { ...parsed } as Record<string, unknown>;
}

export function advancedConfiguration(kind: EndpointKind, value: unknown) {
  const configuration = configurationRecord(value);
  const structuredKeys: Record<EndpointKind, Set<string>> = {
    llm: new Set(["name", "kind", "base_url", "weight", "timeout_seconds", "models"]),
    mcp: new Set([
      "name",
      "trust_domain",
      "transport",
      "program",
      "args",
      "env",
      "url",
      "bearer_token"
    ]),
    a2a: new Set(["name", "url", "bearer_token"])
  };
  return Object.fromEntries(
    Object.entries(configuration).filter(([key]) => !structuredKeys[kind].has(key))
  );
}

export function buildLlmConfiguration(
  base: Record<string, unknown>,
  fields: {
    name: string;
    kind: string;
    baseUrl: string;
    weight: number;
    timeoutSeconds: number;
    models: LlmModel[];
  }
) {
  return {
    ...base,
    name: fields.name,
    kind: fields.kind,
    base_url: fields.baseUrl,
    weight: fields.weight,
    timeout_seconds: fields.timeoutSeconds,
    models: z.array(llmModelSchema).parse(fields.models)
  };
}

export function buildMcpConfiguration(
  base: Record<string, unknown>,
  fields:
    | {
        name: string;
        transport: "stdio";
        trustDomain: string;
        program: string;
        args: string[];
        env: object;
      }
    | {
        name: string;
        transport: "streamable_http" | "legacy_sse";
        trustDomain: string;
        url: string;
      }
) {
  const configuration: Record<string, unknown> = {
    ...base,
    name: fields.name,
    trust_domain: fields.trustDomain,
    transport: fields.transport
  };
  delete configuration.bearer_token;
  if (fields.transport === "stdio") {
    Object.assign(configuration, {
      program: fields.program,
      args: fields.args,
      env: fields.env
    });
    delete configuration.url;
  } else {
    configuration.url = fields.url;
    configuration.bearer_token = null;
    delete configuration.program;
    delete configuration.args;
    delete configuration.env;
  }
  return configuration;
}

export function buildA2aConfiguration(base: Record<string, unknown>, name: string, url: string) {
  const configuration: Record<string, unknown> = { ...base, name, url };
  delete configuration.bearer_token;
  return configuration;
}
